import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { AlertTriangle, X, RefreshCw, Clock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Support Renewal Banner Component
 * 
 * Displays renewal reminders in the app header (Owner only)
 * - Shows at 90, 60, 30 days before expiry
 * - Persistent red banner when expired (cannot dismiss)
 * - Dismissible for non-urgent reminders (>30 days)
 */
export default function SupportRenewalBanner() {
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);

  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const isOwner = user.role === 'admin';

  useEffect(() => {
    if (isOwner) {
      fetchBanner();
    } else {
      setLoading(false);
    }
  }, [isOwner]);

  const fetchBanner = async () => {
    try {
      const res = await axios.get(`${API}/pscl/renewal-banner`);
      setBanner(res.data);
    } catch (error) {
      console.error('Failed to fetch renewal banner:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissBanner = async () => {
    setDismissing(true);
    try {
      await axios.post(`${API}/pscl/renewal-banner/dismiss`);
      setBanner(null);
    } catch (error) {
      console.error('Cannot dismiss banner:', error);
    } finally {
      setDismissing(false);
    }
  };

  if (loading || !banner) {
    return null;
  }

  const getBannerStyles = () => {
    switch (banner.type) {
      case 'error':
        return 'bg-red-900/90 border-red-700 text-red-100';
      case 'warning':
        return 'bg-yellow-900/90 border-yellow-700 text-yellow-100';
      case 'info':
        return 'bg-blue-900/90 border-blue-700 text-blue-100';
      default:
        return 'bg-gray-900/90 border-gray-700 text-gray-100';
    }
  };

  const getIcon = () => {
    switch (banner.type) {
      case 'error':
        return <AlertTriangle className="w-5 h-5" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  return (
    <div 
      className={`w-full px-4 py-3 border-b ${getBannerStyles()}`}
      data-testid="support-renewal-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          {getIcon()}
          <div>
            <span className="font-semibold">{banner.title}</span>
            <span className="mx-2">•</span>
            <span>{banner.message}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {banner.action_text && (
            <Button 
              size="sm"
              className={
                banner.type === 'error' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : banner.type === 'warning'
                  ? 'bg-yellow-600 hover:bg-yellow-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }
              onClick={() => window.location.href = '/dashboard/pscl-admin'}
            >
              {banner.action_text}
            </Button>
          )}
          
          {banner.dismissible && (
            <button
              onClick={dismissBanner}
              className="p-1 rounded hover:bg-white/10 transition-colors"
              disabled={dismissing}
            >
              {dismissing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <X className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Reactivation Fee Preview */}
      {banner.reactivation_fee && (
        <div className="max-w-7xl mx-auto mt-2 text-sm opacity-90">
          <span>Reactivation fee required: </span>
          <span className="font-semibold">
            RM {banner.reactivation_fee.total_to_pay?.toLocaleString()}
          </span>
          <span className="text-xs ml-2">
            ({banner.reactivation_fee.missed_years} years missed × 25% penalty + current year fee)
          </span>
        </div>
      )}
    </div>
  );
}
